import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { motion } from 'framer-motion';
import { Car, CalendarBlank, CurrencyEur, Star, ClipboardText, User, SignOut } from '@phosphor-icons/react';

function MemberSidebar() {
  const { user, logout } = useAuth();
  const links = [
    { label: 'Tableau de bord', path: '/espace-membre/dashboard', icon: ClipboardText },
    { label: 'Mes reservations', path: '/espace-membre/reservations', icon: Car },
    { label: 'Mon profil', path: '/espace-membre/profil', icon: User },
  ];
  return (
    <aside className="w-full lg:w-64 shrink-0" data-testid="member-sidebar">
      <div className="bg-desti-surface border border-white/5 p-6">
        <p className="text-sm text-desti-gold mb-1">Bienvenue</p>
        <p className="text-white font-heading text-lg">{user?.firstName} {user?.lastName}</p>
        <div className="line-gold my-4" />
        <nav className="space-y-1">
          {links.map(l => (
            <Link key={l.path} to={l.path}
              className={`flex items-center gap-3 px-3 py-2.5 text-sm transition-colors ${window.location.pathname === l.path ? 'text-desti-gold bg-desti-gold/5' : 'text-white/60 hover:text-white'}`}
              data-testid={`sidebar-${l.path.split('/').pop()}`}>
              <l.icon size={18} weight="duotone" /> {l.label}
            </Link>
          ))}
          <button onClick={logout} className="flex items-center gap-3 px-3 py-2.5 text-sm text-white/40 hover:text-white transition-colors w-full" data-testid="sidebar-logout">
            <SignOut size={18} weight="duotone" /> Deconnexion
          </button>
        </nav>
      </div>
    </aside>
  );
}

export default function MemberDashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get('/member/dashboard').then(r => setStats(r.data)).catch(() => {});
  }, []);

  return (
    <div className="pt-20 min-h-screen" data-testid="member-dashboard-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <div className="flex flex-col lg:flex-row gap-8">
          <MemberSidebar />
          <div className="flex-1">
            <h1 className="font-heading text-3xl text-white mb-8">Tableau de bord</h1>
            {stats && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                {[
                  { icon: CalendarBlank, label: 'Courses a venir', value: stats.upcomingBookings, color: 'text-blue-400' },
                  { icon: Car, label: 'Courses effectuees', value: stats.pastBookings, color: 'text-green-400' },
                  { icon: CurrencyEur, label: 'Total depense', value: `${stats.totalSpent.toFixed(2)} EUR`, color: 'text-desti-gold' },
                ].map((s, i) => (
                  <motion.div key={s.label} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}
                    className="bg-desti-surface border border-white/5 p-6" data-testid={`dashboard-stat-${i}`}>
                    <s.icon size={24} weight="duotone" className={`${s.color} mb-3`} />
                    <p className="text-2xl text-white font-heading">{s.value}</p>
                    <p className="text-xs text-desti-text mt-1">{s.label}</p>
                  </motion.div>
                ))}
              </div>
            )}
            <div className="mt-8 flex gap-4">
              <Link to="/espace-membre/reservations" className="bg-desti-gold text-desti-black font-medium text-sm px-6 py-3 hover:bg-desti-gold-hover transition-colors" data-testid="view-bookings-link">
                Voir mes reservations
              </Link>
              <Link to="/" className="border border-white/20 text-white text-sm px-6 py-3 hover:bg-white/5 transition-colors" data-testid="new-booking-link">
                Nouvelle reservation
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export { MemberSidebar };
